import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../utils/cn';

interface PageHeaderProps {
  /** Page or entity title. */
  title: ReactNode;
  /** One-line description under the title. */
  description?: ReactNode;
  /** Small uppercase kicker above the title (usually the nav category). */
  eyebrow?: ReactNode;
  /** Right-aligned actions (primary CTA first). */
  actions?: ReactNode;
  /** Small inline items beside the title: live dot, cache status, counts. */
  meta?: ReactNode;
  /** Badges rendered inline after the title (status, priority). */
  titleAdornment?: ReactNode;
  /** Optional leading icon rendered in a tinted tile. */
  icon?: ComponentType<{ className?: string }>;
  /** Back link for detail pages. */
  backTo?: string;
  backLabel?: string;
  onBack?: () => void;
  /** Toolbar or tabs rendered under the header row. */
  children?: ReactNode;
  className?: string;
}

/**
 * Standard page header: eyebrow, title, description on the left; actions on the
 * right. Lives outside any card. Detail pages add a back link and badges.
 */
export const PageHeader = ({
  title,
  description,
  eyebrow,
  actions,
  meta,
  titleAdornment,
  icon: Icon,
  backTo,
  backLabel,
  onBack,
  children,
  className,
}: PageHeaderProps) => {
  const backContent = (
    <>
      <ArrowLeft className="w-3.5 h-3.5" />
      <span>{backLabel ?? 'Back'}</span>
    </>
  );
  const backClass = 'inline-flex items-center gap-1.5 text-xs font-medium text-glass-400 hover:text-white mb-2 -ml-0.5 rounded';

  return (
    <header className={cn('flex flex-col gap-4', className)}>
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-start md:justify-between">
        <div className="min-w-0 flex-1 md:basis-64">
          {backTo ? (
            <Link to={backTo} className={backClass}>{backContent}</Link>
          ) : onBack ? (
            <button type="button" onClick={onBack} className={backClass}>{backContent}</button>
          ) : null}
          {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0">
            {Icon && (
              <span className="icon-tile icon-tile-accent">
                <Icon className="w-4 h-4" />
              </span>
            )}
            <h1 className="text-xl md:text-2xl font-semibold tracking-tight text-white min-w-0 break-words">
              {title}
            </h1>
            {titleAdornment && (
              <div className="flex flex-wrap items-center gap-1.5">{titleAdornment}</div>
            )}
            {meta && (
              <div className="flex flex-wrap items-center gap-3 text-xs text-glass-400">{meta}</div>
            )}
          </div>
          {description && (
            <p className="mt-1 text-sm text-glass-400 max-w-3xl">{description}</p>
          )}
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 md:justify-end max-w-full">{actions}</div>
        )}
      </div>
      {children}
    </header>
  );
};
